import React from "react";
import { Building2, Database, RefreshCw } from "lucide-react";

import { addAssetWatch, getAssetTrackingOverview, getLiveInstrumentIntraday, getStockDetail, getStockKline, removeAssetWatch } from "../../api/client";
import { Chart } from "../../components/charts";
import { stockIntradayOption, stockKlineOption } from "./stockDetailChartOptions";
import { appendLiveDailyBar, selectStockDisplayQuote } from "./stockDetailState";
import styles from "./stockDetail.module.css";
import { AssetTagEditor } from "../asset-overview/AssetTagEditor";

const PERIODS = [["intraday", "分时"], ["daily", "日K"], ["weekly", "周K"], ["monthly", "月K"]];

function format(value, digits = 2) {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("zh-CN", { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : "--";
}

function signed(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${parsed > 0 ? "+" : ""}${parsed.toFixed(2)}%` : "--";
}

function yi(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${format(parsed / 10000, 1)} 亿` : "--";
}

export function StockDetailPage({ instrument, navigate, onStatus, snapshotId }) {
  const requestedReturnTo = new URLSearchParams(window.location.search).get("from");
  const returnTo = requestedReturnTo === "/overview"
    || requestedReturnTo?.startsWith("/industry/")
    || requestedReturnTo === "/rank"
    || requestedReturnTo === "/market"
    ? requestedReturnTo
    : "/market";
  const returnLabel = returnTo.startsWith("/industry/")
    ? "返回行业详情"
    : returnTo === "/rank" ? "返回选股结果" : returnTo === "/overview" ? "返回概览" : "返回大盘";
  const [detail, setDetail] = React.useState(null);
  const [liveQuote, setLiveQuote] = React.useState(null);
  const [series, setSeries] = React.useState(null);
  const [period, setPeriod] = React.useState("daily");
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(true);
  const [watching, setWatching] = React.useState(false);
  const [watchBusy, setWatchBusy] = React.useState(false);
  const [watchNotice, setWatchNotice] = React.useState("");
  const [watchError, setWatchError] = React.useState("");

  const loadDetail = React.useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const payload = await getStockDetail({ snapshotId, code: instrument.code });
      setDetail(payload);
      onStatus?.({ status: "ok", dataDate: payload.meta.data_date });
    } catch (caught) {
      setError(caught.message || "个股详情暂时无法读取");
      onStatus?.({ status: "unavailable", dataDate: null });
    } finally {
      setBusy(false);
    }
  }, [instrument.code, onStatus, snapshotId]);

  const loadLiveQuote = React.useCallback(async () => {
    try {
      const payload = await getLiveInstrumentIntraday({ code: instrument.code, isIndex: false });
      setLiveQuote({ quote: payload.data.quote, meta: payload.meta ?? {} });
    } catch {
      setLiveQuote(null);
    }
  }, [instrument.code]);

  const loadSeries = React.useCallback(async () => {
    try {
      if (period === "intraday") {
        const payload = await getLiveInstrumentIntraday({ code: instrument.code, isIndex: false });
        const points = payload.data.series?.[0]?.points ?? [];
        setLiveQuote({ quote: payload.data.quote, meta: payload.meta ?? {} });
        if (payload.meta?.series_status === "unavailable" || !points.length) {
          setSeries({
            error: payload.meta?.series_reason || "分钟行情源暂时不可用，头部行情仍按本地批次更新。",
          });
          return;
        }
        setSeries({
          kind: "intraday",
          points,
          meta: payload.meta ?? {},
        });
      } else {
        const payload = await getStockKline({ snapshotId, code: instrument.code, period });
        setSeries({ kind: "kline", bars: payload.data.bars ?? [] });
      }
    } catch (caught) {
      setSeries({ error: caught.message || "行情序列暂时不可用" });
    }
  }, [instrument.code, period, snapshotId]);

  React.useEffect(() => { void loadDetail(); }, [loadDetail]);
  React.useEffect(() => {
    let cancelled = false;
    void getAssetTrackingOverview().then((overview) => {
      if (!cancelled) setWatching((overview.watches ?? []).some((item) => item.asset_code === instrument.code));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [instrument.code]);

  async function toggleWatch() {
    if (watchBusy) return;
    setWatchBusy(true);
    setWatchError("");
    try {
      if (watching) {
        await removeAssetWatch(instrument.code);
        setWatching(false);
        setWatchNotice("已移出关注");
      } else {
        await addAssetWatch(instrument.code);
        setWatching(true);
        setWatchNotice("已加入关注，可在概览查看");
      }
    } catch (caught) {
      setWatchError(caught.message || "暂时无法更新关注状态，请稍后重试。");
    } finally {
      setWatchBusy(false);
    }
  }
  React.useEffect(() => {
    void loadLiveQuote();
    const timer = window.setInterval(() => {
      if (!document.hidden) void loadLiveQuote();
    }, 20000);
    return () => window.clearInterval(timer);
  }, [loadLiveQuote]);
  React.useEffect(() => { void loadSeries(); }, [loadSeries]);
  React.useEffect(() => {
    if (period !== "intraday") return undefined;
    const timer = window.setInterval(() => { if (!document.hidden) void loadSeries(); }, 20000);
    return () => window.clearInterval(timer);
  }, [loadSeries, period]);

  if (busy && !detail) return <div className={styles.state}><RefreshCw className={styles.spin} size={20} />正在读取个股详情</div>;
  if (!detail) return <div className={styles.state}><Database size={20} />{error}<button onClick={loadDetail}>重新读取</button></div>;

  const { identity, quote, valuation, financial, range_52w: range } = detail.data;
  const currentSessionQuote = liveQuote?.quote;
  const {
    isLiveSession: hasLiveSessionQuote,
    quote: displayQuote,
  } = selectStockDisplayQuote(quote, currentSessionQuote);
  const displayPrice = Number(displayQuote.close);
  const displayChange = Number.isFinite(Number(displayQuote.change))
    ? Number(displayQuote.change)
    : displayPrice - Number(displayQuote.pre_close);
  const displayPct = Number.isFinite(Number(displayQuote.pct_chg))
    ? Number(displayQuote.pct_chg)
    : Number(displayQuote.pre_close) ? displayChange / Number(displayQuote.pre_close) * 100 : null;
  const up = displayPct >= 0;
  const intradayPreviousClose = hasLiveSessionQuote
    ? currentSessionQuote.pre_close
    : series?.meta?.trade_date > quote.trade_date
    ? quote.close
    : quote.pre_close;
  const chartBars = period === "daily" && hasLiveSessionQuote
    ? appendLiveDailyBar(series?.bars ?? [], currentSessionQuote)
    : series?.bars ?? [];
  const chart = series?.kind === "intraday"
    ? stockIntradayOption(series.points, intradayPreviousClose, identity.name)
    : stockKlineOption(chartBars);
  const liveAsOf = currentSessionQuote?.as_of_ts
    ? currentSessionQuote.as_of_ts.replace("T", " ").slice(0, 16)
    : null;
  const quoteSource = currentSessionQuote?.source_id === "tencent_quote"
    ? "腾讯实时批次"
    : currentSessionQuote?.source_id;

  return (
    <div className={styles.stockPage}>
      <nav className={styles.breadcrumb} aria-label="路径">
        <button type="button" onClick={() => navigate(returnTo)}>{returnTo === "/overview" ? "概览" : returnLabel}</button>
        <span>/</span>
        <span>{identity.name}</span>
      </nav>
      <section className={styles.quoteCard}>
        <div className={styles.identity}>
          <div className={styles.title}><h1>{identity.name}</h1><span className={styles.code}>{identity.ts_code}</span><em>{identity.market}</em></div>
          <div className={styles.identityActions}><AssetTagEditor assetCode={identity.ts_code} inline /><button className={styles.watchButton} type="button" onClick={() => { void toggleWatch(); }} disabled={watchBusy}>{watchBusy ? "处理中…" : watching ? "取消关注" : "加入关注"}</button></div>
          {(watchNotice || watchError) && <p className={watchError ? styles.watchError : styles.watchNotice} aria-live="polite">{watchError || watchNotice}</p>}
        </div>
        <div className={`${styles.price} ${up ? styles.priceUp : styles.priceDown}`}>
          <strong>{format(displayPrice)}</strong><span>{format(displayChange)}　{signed(displayPct)}</span>
          <small>{hasLiveSessionQuote
            ? `${liveAsOf || displayQuote.trade_date} · 当日行情 · ${quoteSource || "本地实时批次"}`
            : `${displayQuote.trade_date} 收盘 · 本地已发布行情`}</small>
        </div>
        <div className={styles.metrics}>
          <div><span>开盘</span><b>{format(displayQuote.open)}</b></div><div><span>最高</span><b>{format(displayQuote.high)}</b></div>
          <div><span>最低</span><b>{format(displayQuote.low)}</b></div><div><span>昨收</span><b>{format(displayQuote.pre_close)}</b></div>
          <div><span>成交额</span><b>{yi(Number(displayQuote.amount_cny) / 10000)}</b></div><div><span>换手率</span><b>{format(hasLiveSessionQuote ? displayQuote.turnover_rate : valuation?.turnover_rate)}%</b></div>
        </div>
      </section>

      <section className={styles.chartCard}>
        <div className={styles.chartHead}>
          <div><h2>行情走势</h2><p>历史行情读取 market_new.db；头部行情来自本地批次，分时图读取真实一分钟行情。</p></div>
          <div className={styles.tabs}>{PERIODS.map(([value, label]) => <button key={value} className={period === value ? styles.active : ""} onClick={() => setPeriod(value)}>{label}</button>)}</div>
        </div>
        {series?.error ? <div className={styles.chartState}>{series.error}</div> : <div className={styles.chart}><Chart option={chart} /></div>}
        <footer><Database size={14} /> {period === "intraday"
          ? `${series?.meta?.source_id === "tencent" ? "腾讯" : series?.meta?.source_id === "sina" ? "新浪" : "分钟行情"} · 1 分钟序列 · 55 秒服务缓存`
          : `market_new.db · 数据日期 ${detail.meta.data_date}`}</footer>
      </section>

      <div className={styles.infoGrid}>
        <section className={styles.infoCard}><h2><Building2 size={18} />公司与估值</h2>
          <dl><div><dt>行业</dt><dd>{identity.industry || "--"}</dd></div><div><dt>上市日期</dt><dd>{identity.list_date || "--"}</dd></div>
            <div><dt>PE TTM</dt><dd>{format(valuation?.pe_ttm)}</dd></div><div><dt>PB</dt><dd>{format(valuation?.pb)}</dd></div>
            <div><dt>总市值</dt><dd>{yi(valuation?.total_mv)}</dd></div><div><dt>流通市值</dt><dd>{yi(valuation?.circ_mv)}</dd></div></dl>
        </section>
        <section className={styles.infoCard}><h2>经营概览</h2>
          <dl><div><dt>ROE</dt><dd>{format(financial?.roe)}%</dd></div><div><dt>毛利率</dt><dd>{format(financial?.gross_margin)}%</dd></div>
            <div><dt>营收同比</dt><dd>{signed(financial?.revenue_yoy)}</dd></div><div><dt>利润同比</dt><dd>{signed(financial?.profit_yoy)}</dd></div>
            <div><dt>52周最高</dt><dd>{format(range?.high)}</dd></div><div><dt>52周最低</dt><dd>{format(range?.low)}</dd></div></dl>
          <small>财务披露期 {financial?.end_date || "--"} · 公告日 {financial?.ann_date || "--"}</small>
        </section>
      </div>
    </div>
  );
}
